import { useState, useEffect } from 'react';
import axios from 'axios';
import { getApiUrl } from './envValidator';

const API_URL = getApiUrl();

// Default mappings from our migration
const defaultMappings = {
  "migrationDate": "2025-06-13T20:05:18.564Z",
  "images": {
    "TMZ Icon.png": {
      "cloudflareId": "images/1749845116894-tmuhzm5cssd.png",
      "url": "cdn.themadzone.com/images/1749845116894-tmuhzm5cssd.png"
    },
    "TMZ L.png": {
      "cloudflareId": "images/1749845117190-tnvd3ht8el.png",
      "url": "cdn.themadzone.com/images/1749845117190-tnvd3ht8el.png"
    },
    "avatar.svg": {
      "cloudflareId": "images/1749845117356-jd9bplluypb.svg",
      "url": "cdn.themadzone.com/images/1749845117356-jd9bplluypb.svg"
    },
    "coin.png": {
      "cloudflareId": "images/1749845117529-vy6k74u1bto.png",
      "url": "cdn.themadzone.com/images/1749845117529-vy6k74u1bto.png"
    },
    "coming_soon.webp": {
      "cloudflareId": "images/1749845117966-1xc5mt0rxdc.webp",
      "url": "cdn.themadzone.com/images/1749845117966-1xc5mt0rxdc.webp"
    },
    "reclaim_supremacy.png": {
      "cloudflareId": "images/1749845118221-te1y8iqxai.png",
      "url": "cdn.themadzone.com/images/1749845118221-te1y8iqxai.png"
    },
    "shirt.jpg": {
      "cloudflareId": "images/1749845118393-6qgl01joybm.jpg",
      "url": "cdn.themadzone.com/images/1749845118393-6qgl01joybm.jpg"
    }
  }
};

// In-memory cache for image mappings
let imageMappings = defaultMappings;

/**
 * Fetch image mappings from the API
 * @returns {Promise<Object>} - The image mappings
 */
export const fetchImageMappings = async () => {
  try {
    const response = await axios.get(`${API_URL}/api/images/mappings`);
    if (response.data.success) {
      imageMappings = response.data.mappings;
      return imageMappings;
    }
    return defaultMappings;
  } catch (error) {
    console.error('Error fetching image mappings:', error);
    return defaultMappings;
  }
};

/**
 * Convert a local image path to its Cloudflare R2 URL
 * @param {string} localPath - The local image path (e.g., '/images/logo.png')
 * @returns {string} - The Cloudflare R2 URL or original path if not found
 */
export const getCloudflareImageUrl = (localPath) => {
  if (!localPath) return '';
  
  // If it's already a Cloudflare URL, return it as is
  if (localPath.startsWith('https://cdn.themadzone.com')) {
    return localPath;
  }
  
  // Extract the filename from the path
  const filename = localPath.split('/').pop();
  
  // Look up the filename in the mapping
  if (imageMappings.images && imageMappings.images[filename]) {
    return `https://${imageMappings.images[filename].url}`;
  }
  
  // Return the original path if not found
  return localPath;
};

/**
 * Get all available Cloudflare image mappings
 * @returns {Object} - Object with local filenames as keys and Cloudflare URLs as values
 */
export const getCloudflareImageMappings = () => {
  const mappings = {};
  
  if (imageMappings.images) {
    Object.keys(imageMappings.images).forEach(filename => {
      mappings[filename] = `https://${imageMappings.images[filename].url}`;
    });
  }
  
  return mappings;
};

/**
 * React hook to use Cloudflare image mappings
 * @returns {Object} - Object containing mappings and loading state
 */
export const useCloudflareImageMappings = () => {
  const [mappings, setMappings] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    const loadMappings = async () => {
      try {
        setLoading(true);
        const result = await fetchImageMappings();
        const formattedMappings = {};
        
        if (result.images) {
          Object.keys(result.images).forEach(filename => {
            formattedMappings[filename] = `https://${result.images[filename].url}`;
          });
        }
        
        setMappings(formattedMappings);
        setError(null);
      } catch (err) {
        setError(err.message || 'Failed to load image mappings');
      } finally {
        setLoading(false);
      }
    };
    
    loadMappings();
  }, []);
  
  return { mappings, loading, error };
};

const imageUtils = {
  getCloudflareImageUrl,
  getCloudflareImageMappings,
  fetchImageMappings,
  useCloudflareImageMappings
};

export default imageUtils; 